import { Injectable } from '@angular/core';
import { INotification } from '@app/core/interfaces';
import { environment } from '@env/environment';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationHubService {
  private hubConnection?: HubConnection;
  private startPromise?: Promise<void>;
  private currentGroup: string | null = null;

  private readonly newNotificationSubject = new Subject<INotification>();
  private readonly markAllAsReadSubject = new Subject<void>();

  public readonly newNotification$: Observable<INotification> = this.newNotificationSubject.asObservable();
  public readonly markAllAsRead$: Observable<void> = this.markAllAsReadSubject.asObservable();

  constructor(private authService: AuthenticationService) {
    this.authService.currentUser.subscribe(() => this.startForCurrentUser());
    this.authService.userActiveType$.subscribe(() => this.startForCurrentUser());
  }

  startForCurrentUser(): void {
    const nextGroup = this.getCurrentGroup();
    if (!nextGroup && !this.currentGroup) {
      return;
    }

    this.ensureConnected()
      .then(() => this.syncGroup())
      .catch((err) => console.error('Error while establishing notification hub connection', err));
  }

  private ensureConnected(): Promise<void> {
    if (!this.hubConnection) {
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${environment.apiUrl}notify`)
        .withAutomaticReconnect()
        .build();

      this.hubConnection.on('MarkAllAsRead', () => {
        this.markAllAsReadSubject.next();
      });

      this.hubConnection.on('NewNotification', (data: INotification) => {
        this.newNotificationSubject.next(data);
      });

      this.hubConnection.onreconnected(() => {
        this.syncGroup();
      });
    }

    if (this.hubConnection.state === HubConnectionState.Connected) {
      return Promise.resolve();
    }

    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.hubConnection
      .start()
      .then(() => undefined)
      .finally(() => {
        this.startPromise = undefined;
      });

    return this.startPromise;
  }

  private syncGroup(): void {
    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      return;
    }

    const nextGroup = this.getCurrentGroup();
    if (!nextGroup) {
      if (!this.currentGroup) {
        return;
      }

      const previousGroup = this.currentGroup;
      this.currentGroup = null;
      this.hubConnection.invoke('LeaveGroup', previousGroup).catch((err) => {
        console.error('Failed to leave notification hub group', err);
      });
      return;
    }

    if (nextGroup === this.currentGroup) {
      return;
    }

    const previousGroup = this.currentGroup;
    this.currentGroup = nextGroup;

    const operations: Promise<unknown>[] = [];
    if (previousGroup) {
      operations.push(this.hubConnection.invoke('LeaveGroup', previousGroup));
    }

    operations.push(this.hubConnection.invoke('JoinGroup', nextGroup));

    Promise.all(operations).catch((err) => {
      console.error('Failed to sync notification hub group', err);
    });
  }

  private getCurrentGroup(): string | null {
    const userId = this.authService.currentUserValue?.id;
    const userType = this.authService.currentUserActiveType;
    if (!userId || !userType) {
      return null;
    }

    return `${userId}-${userType}`;
  }
}
